import React from "react";

import { css } from "@emotion/css";
import { Outlet, useMatchRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import { Icons } from "@/assets/icons.ts";
import { useKeycloak } from "@/auth/KeycloakAuthState.ts";
import { usePostHogTrackPageView } from "@/hooks/usePostHogTrackPageView.ts";
import { useStyles } from "@/hooks/useStyles.ts";
import { Queries } from "@/queries/Queries.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { ScreenMax, ScreenMin } from "@/theme/responsive.ts";
import { PageDrawerProvider } from "@/uiKit/atoms/page/PageDrawerState.ts";
import { ImpersonatingBanner } from "@/uiKit/molecules/ImpersonationBanner.tsx";
import { BottomNavigationBar } from "@/uiKit/molecules/navigation/BottomNavigationBar.tsx";
import { NavigationItem } from "@/uiKit/molecules/navigation/NavigationItem.tsx";
import { SideNavigationBar } from "@/uiKit/molecules/navigation/SideNavigationBar.tsx";
import { UserMenu } from "@/uiKit/molecules/navigation/UserMenu.tsx";

export const PractitionerRootTemplate: React.FC = () => {
  usePostHogTrackPageView();
  const { data: featureFlags } = Queries.user.useFeatureFlags({});
  const me = Queries.user.usePractitionerMe({});
  const keycloak = useKeycloak(state => state.keycloak);

  const styles = useStyles(makeStyles);
  const { t, i18n } = useTranslation();
  const routeMatches = useMatchRoute();

  // Both navigation bars are always mounted (CSS picks one), so the mobile copy gets suffixed ids.
  const userMenu = (idSuffix: string) => (
    <UserMenu
      firstName={me.firstName}
      lastName={me.lastName}
      profilePictureUrl={me.profilePicture?.url}
      onDisconnect={() => keycloak.logout({ redirectUri: window.origin })}
      idSuffix={idSuffix}
    />
  );
  const navItems = (idSuffix: string) => (
    <>
      <NavigationItem
        id={`patient-list-nav-item${idSuffix}`}
        data-action="navigation.main.list-patients"
        to="/patients"
        Icon={Icons.users}
        isCurrent={!!routeMatches({ to: "/patients", fuzzy: true })}
        tooltip={t("navbar.myPatients")}
      />
      {featureFlags?.["billing-info-page"] ? (
        <NavigationItem
          id={`practitioner-billing-nav-item${idSuffix}`}
          data-action="navigation.main.billing"
          to="/practitioner-billing"
          Icon={Icons.receipt}
          isCurrent={!!routeMatches({ to: "/practitioner-billing" })}
          tooltip={t("navbar.practitionerBilling")}
        />
      ) : null}
      {featureFlags?.["patient-sync"] && me.qualification === "doctor" ? (
        <NavigationItem
          id={`patient-sync-nav-item${idSuffix}`}
          data-action="navigation.main.patient-sync"
          to="/patient-sync"
          Icon={Icons.sync}
          isCurrent={!!routeMatches({ to: "/patient-sync", fuzzy: true })}
          tooltip={t("navbar.patientSync")}
        />
      ) : null}
    </>
  );

  return (
    <PageDrawerProvider
      chat={{
        isOpen: false,
        isEnabled: true,
      }}
      inclusionCenter={{
        isOpen: false,
        isEnabled: false,
      }}
    >
      <ImpersonatingBanner />
      <div id="practitioner-root-template" lang={i18n.language} className={styles.container}>
        <SideNavigationBar userMenu={userMenu("")} className={styles.sidebar}>
          {navItems("")}
        </SideNavigationBar>
        <main id="practitioner-root-template-content" className={styles.content}>
          <Outlet />
        </main>
        <BottomNavigationBar userMenu={userMenu("-mobile")} className={styles.bottomBar}>
          {navItems("-mobile")}
        </BottomNavigationBar>
      </div>
    </PageDrawerProvider>
  );
};

const makeStyles = (theme: Theme) => ({
  container: css`
    display: flex;
    flex-direction: column;
    margin: 0;
    padding: 0;
    width: 100%;
    background-color: ${theme.palette.background.default};
  `,
  sidebar: css`
    ${ScreenMax.SM} {
      display: none;
    }
  `,
  bottomBar: css`
    ${ScreenMin.SM} {
      display: none;
    }
  `,
  content: css`
    flex: 1 1;
    overflow-x: clip;

    ${ScreenMin.SM} {
      margin-left: ${theme.spacing(30)};
    }
    ${ScreenMax.SM} {
      margin-bottom: ${theme.spacing(30)};
    }
  `,
});
